import { useState } from "react";
import { useListContext } from "ra-core";
import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { Card, CardContent } from "@/components/ui/card";
import {
  ExternalLink,
  FileText,
  Award,
  FileSpreadsheet,
  File,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

import type { SalesIntel, SalesTrigger, EnrichmentDocument } from "../types";
import { ConfidenceBadge } from "./ConfidenceBadge";

export const SalesIntelTab = () => (
  <div className="flex flex-col gap-6 pt-2">
    <ReferenceManyField
      reference="sales_intel"
      target="company_id"
      sort={{ field: "id", order: "ASC" }}
    >
      <IntelContent />
    </ReferenceManyField>
    <ReferenceManyField
      reference="sales_triggers"
      target="company_id"
      sort={{ field: "id", order: "ASC" }}
    >
      <TriggersContent />
    </ReferenceManyField>
  </div>
);

const FitBadge = ({ value }: { value?: string }) => {
  if (!value) return <span>---</span>;
  const upper = value.toUpperCase();
  const colorMap: Record<string, string> = {
    STRONG: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    MODERATE:
      "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
    WEAK: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  };
  const level = upper.split(/[\s\-\u2013\u2014]/)[0].trim();
  const colors =
    colorMap[level] ||
    "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors}`}
    >
      {value}
    </span>
  );
};

const DOC_ICONS: Record<string, typeof FileText> = {
  audit_pdf: FileText,
  certificate: Award,
  sales_brief: FileSpreadsheet,
};

const DocumentsList = ({ documents }: { documents: EnrichmentDocument[] }) => {
  if (!documents.length) return null;

  return (
    <div className="mt-4">
      <h6 className="text-sm font-semibold border-b pb-1 mb-2">Documents</h6>
      <div className="flex flex-col gap-1">
        {documents.map((doc, i) => {
          const Icon = DOC_ICONS[doc.type] || File;
          return (
            <a
              key={`${doc.type}-${i}`}
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-primary hover:underline py-1"
            >
              <Icon className="h-4 w-4 shrink-0" />
              {doc.name}
              <ExternalLink className="h-3 w-3 shrink-0" />
            </a>
          );
        })}
      </div>
    </div>
  );
};

const IntelCard = ({ intel }: { intel: SalesIntel }) => (
  <Card>
    <CardContent>
      <div className="grid grid-cols-2 gap-x-8 gap-y-4 text-sm">
        <Section title="Account Snapshot">
          <Field label="Scale" value={intel.scale} />
          <Field label="Complexity" value={intel.complexity} />
          <Field label="Yield Potential" value={intel.yield_potential} />
        </Section>

        <Section title="Sales Approach">
          <Field label="Primary Angle" value={intel.primary_angle} />
          <Field label="Entry Hook" value={intel.entry_hook} />
          <Field label="Decision Maker" value={intel.decision_maker} />
          <Field label="Urgency Driver" value={intel.urgency_driver} />
          <Field label="Pilot Scope" value={intel.pilot_scope} />
        </Section>

        <Section title="Tanalink Fit">
          <div className="flex flex-col">
            <span className="text-muted-foreground">Harvesting</span>
            <FitBadge value={intel.fit_harvesting} />
          </div>
          <div className="flex flex-col">
            <span className="text-muted-foreground">Fertilizer</span>
            <FitBadge value={intel.fit_fertilizer} />
          </div>
          <div className="flex flex-col">
            <span className="text-muted-foreground">Productivity</span>
            <FitBadge value={intel.fit_productivity} />
          </div>
        </Section>

        <Section title="Scorecard">
          <div className="flex flex-col">
            <span className="text-muted-foreground">Confidence</span>
            <ConfidenceBadge value={intel.confidence_score} />
          </div>
          <Field label="Why Now" value={intel.why_now} />
          <Field label="Pilot Modules" value={intel.pilot_modules} />
          <Field label="Leadership Targets" value={intel.leadership_targets} />
          <Field label="Email Subject" value={intel.email_subject} />
        </Section>
      </div>

      {intel.discovery_questions && (
        <div className="mt-6">
          <h6 className="text-sm font-semibold border-b pb-1 mb-2">
            Discovery Questions
          </h6>
          <p className="text-sm whitespace-pre-line">
            {intel.discovery_questions}
          </p>
        </div>
      )}

      {intel.sales_brief_link && !intel.documents?.length && (
        <div className="mt-4">
          <a
            href={intel.sales_brief_link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Sales Brief
          </a>
        </div>
      )}

      {intel.documents && intel.documents.length > 0 && (
        <DocumentsList documents={intel.documents} />
      )}
    </CardContent>
  </Card>
);

const TrackIdSection = ({
  trackId,
  intel,
  defaultOpen,
}: {
  trackId: string;
  intel: SalesIntel;
  defaultOpen: boolean;
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border rounded-md">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium hover:bg-muted/50"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0" />
        )}
        <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded">
          {trackId}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4">
          <IntelCard intel={intel} />
        </div>
      )}
    </div>
  );
};

const IntelContent = () => {
  const { data, isPending } = useListContext<SalesIntel>();
  if (isPending) return null;

  if (!data?.length) {
    return (
      <div className="p-4 text-sm text-muted-foreground text-center">
        No sales intelligence data yet. Data is generated by the emspo-scraper.
      </div>
    );
  }

  // Single record (legacy or single enrichment unit)
  if (data.length === 1) {
    return <IntelCard intel={data[0]} />;
  }

  // Multiple records: group by track_id
  return (
    <div className="flex flex-col gap-3">
      <h6 className="text-sm font-semibold">
        {data.length} Enrichment Unit{data.length !== 1 ? "s" : ""}
      </h6>
      {data.map((intel, i) => (
        <TrackIdSection
          key={intel.id}
          trackId={intel.track_id || "General"}
          intel={intel}
          defaultOpen={i === 0}
        />
      ))}
    </div>
  );
};

const TriggersContent = () => {
  const { data: triggers, isPending } = useListContext<SalesTrigger>();
  if (isPending) return null;
  if (!triggers?.length) return null;

  return (
    <div>
      <h6 className="text-sm font-semibold mb-2">
        {triggers.length} Sales Trigger
        {triggers.length !== 1 ? "s" : ""}
      </h6>
      <div className="divide-y border rounded-md">
        <div className="grid grid-cols-5 gap-4 px-4 py-2 text-xs font-medium text-muted-foreground">
          <span>Type</span>
          <span className="col-span-2">Finding</span>
          <span>Module</span>
          <span>Impact</span>
        </div>
        {triggers.map((t) => (
          <div
            key={t.id}
            className="grid grid-cols-5 gap-4 px-4 py-3 text-sm items-start"
          >
            <span className="inline-flex">
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                {t.trigger_type || "---"}
              </span>
            </span>
            <div className="col-span-2">
              <div>{t.finding || "---"}</div>
              {t.talk_track && (
                <div className="text-xs text-muted-foreground mt-1 italic">
                  {t.talk_track}
                </div>
              )}
            </div>
            <span>{t.tanalink_module || "---"}</span>
            <span className="text-xs">{t.impact_quantification || "---"}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-2">
    <h6 className="text-base font-semibold border-b pb-1 mb-1">{title}</h6>
    {children}
  </div>
);

const Field = ({ label, value }: { label: string; value?: string | null }) => (
  <div className="flex flex-col">
    <span className="text-muted-foreground">{label}</span>
    <span>{value || "---"}</span>
  </div>
);
